"use client";

import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Plus,
  MagnifyingGlass,
  PencilSimple,
  Trash,
  Check,
  X,
  CaretLeft,
  CaretRight,
} from "@phosphor-icons/react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  getUnits,
  addUnit,
  updateUnit,
  deleteUnit,
  reset,
} from "@/state/units/unit.slice";

// ============================================
// LOADING SPINNER COMPONENT
// ============================================
const LoadingSpinner = ({ size = "default", text }) => (
  <div className="flex items-center justify-center gap-2">
    <Loader2
      className={`animate-spin ${size === "sm" ? "h-4 w-4" : size === "lg" ? "h-8 w-8" : "h-6 w-6"
        }`}
    />
    {text && <span className="text-sm text-muted-foreground">{text}</span>}
  </div>
);

// ============================================
// UNIT FORM COMPONENT (Reusable for Add/Edit)
// ============================================
const UnitForm = ({ unit, onSubmit, onCancel, isLoading, mode = "add" }) => {
  const [formData, setFormData] = useState({
    name: unit?.name || "",
    short_name: unit?.short_name || "",
    allow_decimal: unit?.allow_decimal === 1,
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      return;
    }
    onSubmit({
      ...formData,
      allow_decimal: formData.allow_decimal ? 1 : 0,
      ...(unit?.id && { id: unit.id }),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="name">Unit Name *</Label>
        <Input
          id="name"
          value={formData.name}
          onChange={(e) => handleChange("name", e.target.value)}
          placeholder="e.g. Kilogram, Pieces, Liters"
          required
          disabled={isLoading}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="short_name">Short Name</Label>
        <Input
          id="short_name"
          value={formData.short_name}
          onChange={(e) => handleChange("short_name", e.target.value)}
          placeholder="e.g. kg, pcs, L"
          disabled={isLoading}
        />
      </div>

      <div className="flex items-center space-x-2">
        <Switch
          id="allow_decimal"
          checked={formData.allow_decimal}
          onCheckedChange={(checked) => handleChange("allow_decimal", checked)}
          disabled={isLoading}
        />
        <Label htmlFor="allow_decimal">Allow Decimal Quantities</Label>
      </div>

      <DialogFooter className="pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading || !formData.name.trim()}>
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {mode === "add" ? "Adding..." : "Saving..."}
            </>
          ) : mode === "add" ? (
            "Add Unit"
          ) : (
            "Save Changes"
          )}
        </Button>
      </DialogFooter>
    </form>
  );
};

// ============================================
// UNIT TABLE ROW COMPONENT
// ============================================
const UnitTableRow = ({ unit, onEdit, onDelete, isDeleting }) => {
  return (
    <TableRow className={isDeleting ? "opacity-50" : ""}>
      <TableCell className="font-medium">{unit.name}</TableCell>
      <TableCell>{unit.short_name || "-"}</TableCell>
      <TableCell>
        {unit.allow_decimal ? (
          <Check className="h-4 w-4 text-green-500" />
        ) : (
          <X className="h-4 w-4 text-gray-400 dark:text-gray-500" />
        )}
      </TableCell>
      <TableCell>
        <Badge variant={unit.status === 1 ? "success" : "secondary"}>
          {unit.status === 1 ? "Active" : "Inactive"}
        </Badge>
      </TableCell>
      <TableCell className="text-right space-x-1">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onEdit(unit)}
          disabled={isDeleting}
        >
          <PencilSimple className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onDelete(unit)}
          disabled={isDeleting}
        >
          <Trash className="h-4 w-4 text-red-500" />
        </Button>
      </TableCell>
    </TableRow>
  );
};

// ============================================
// PAGINATION COMPONENT
// ============================================
const Pagination = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
}) => {
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div className="flex items-center justify-between px-4 py-3 border-t">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <span>Rows per page:</span>
        <Select
          value={itemsPerPage.toString()}
          onValueChange={(val) => onItemsPerPageChange(parseInt(val))}
        >
          <SelectTrigger className="w-[70px] h-8">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="5">5</SelectItem>
            <SelectItem value="10">10</SelectItem>
            <SelectItem value="20">20</SelectItem>
            <SelectItem value="50">50</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center gap-4">
        <span className="text-sm text-muted-foreground">
          {totalItems > 0
            ? `${startItem}-${endItem} of ${totalItems}`
            : "0 items"}
        </span>
        <div className="flex gap-1">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage <= 1}
          >
            <CaretLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage >= totalPages}
          >
            <CaretRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// MAIN UNITS PAGE COMPONENT
// ============================================
const UnitsPage = () => {
  const dispatch = useDispatch();
  const { toast } = useToast();

  // Redux state
  const { units, isLoading, success, error, message } = useSelector(
    (state) => state.unit
  );

  // Local state
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Fetch units on mount
  useEffect(() => {
    dispatch(getUnits());
  }, [dispatch]);

  // Handle success/error notifications
  useEffect(() => {
    if (success && message) {
      toast({
        title: "✓ Success",
        description: message,
        className: "bg-green-50 dark:bg-green-900/30 border-green-200",
      });
      dispatch(reset());
      setShowAddDialog(false);
      setShowEditDialog(false);
      setShowDeleteDialog(false);
      setIsSubmitting(false);
      setDeletingId(null);
      dispatch(getUnits());
    }
    if (error) {
      toast({
        title: "✕ Error",
        description: typeof error === "string" ? error : "An error occurred",
        variant: "destructive",
      });
      dispatch(reset());
      setIsSubmitting(false);
      setDeletingId(null);
    }
  }, [success, error, message, toast, dispatch]);

  // Filter units based on search term
  const filteredUnits = useMemo(() => {
    if (!Array.isArray(units)) return [];
    return units.filter(
      (unit) =>
        unit.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        unit.short_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [units, searchTerm]);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Paginated data
  const paginatedUnits = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredUnits.slice(start, start + itemsPerPage);
  }, [filteredUnits, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredUnits.length / itemsPerPage);

  // Summary stats
  const stats = useMemo(() => {
    const unitList = Array.isArray(units) ? units : [];
    return {
      total: unitList.length,
      active: unitList.filter((u) => u.status === 1).length,
      withDecimal: unitList.filter((u) => u.allow_decimal === 1).length,
    };
  }, [units]);

  // Handlers
  const handleAdd = useCallback(
    (data) => {
      setIsSubmitting(true);
      toast({
        title: "Adding unit...",
        description: "Please wait while we create the unit.",
      });
      dispatch(addUnit(data));
    },
    [dispatch, toast]
  );

  const handleEdit = useCallback((unit) => {
    setSelectedUnit(unit);
    setShowEditDialog(true);
  }, []);

  const handleUpdate = useCallback(
    (data) => {
      setIsSubmitting(true);
      toast({
        title: "Updating unit...",
        description: "Please wait while we save changes.",
      });
      dispatch(updateUnit(data));
    },
    [dispatch, toast]
  );

  const handleDeleteClick = useCallback((unit) => {
    setSelectedUnit(unit);
    setShowDeleteDialog(true);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (selectedUnit?.id) {
      setDeletingId(selectedUnit.id);
      setShowDeleteDialog(false);
      toast({
        title: "Deleting unit...",
        description: `Removing "${selectedUnit.name}"...`,
      });
      dispatch(deleteUnit(selectedUnit.id));
    }
  }, [dispatch, selectedUnit, toast]);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleItemsPerPageChange = useCallback((count) => {
    setItemsPerPage(count);
    setCurrentPage(1);
  }, []);

  return (
    <div className="mx-auto p-4 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Units</h1>
          <p className="text-muted-foreground">
            Manage measurement units for your products
          </p>
        </div>
        <Button onClick={() => setShowAddDialog(true)} disabled={isLoading}>
          <Plus className="mr-2 h-4 w-4" /> Add Unit
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Units</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <LoadingSpinner size="sm" />
            ) : (
              <div className="text-2xl font-bold">{stats.total}</div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Active Units</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <LoadingSpinner size="sm" />
            ) : (
              <div className="text-2xl font-bold text-green-600">
                {stats.active}
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">
              Allow Decimals
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <LoadingSpinner size="sm" />
            ) : (
              <div className="text-2xl font-bold text-blue-600">
                {stats.withDecimal}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-sm">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 dark:text-gray-400 dark:text-gray-500" />
          <Input
            className="pl-9"
            placeholder="Search units..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        {isLoading && (
          <div className="flex items-center">
            <LoadingSpinner size="sm" text="Loading..." />
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-slate-900 shadow">
        {isLoading && !units?.length ? (
          <div className="flex flex-col justify-center items-center h-48 gap-4">
            <LoadingSpinner size="lg" />
            <p className="text-muted-foreground">Loading units...</p>
          </div>
        ) : paginatedUnits.length > 0 ? (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Unit Name</TableHead>
                  <TableHead>Short Name</TableHead>
                  <TableHead>Decimals</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedUnits.map((unit) => (
                  <UnitTableRow
                    key={unit.id}
                    unit={unit}
                    onEdit={handleEdit}
                    onDelete={handleDeleteClick}
                    isDeleting={deletingId === unit.id}
                  />
                ))}
              </TableBody>
            </Table>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={filteredUnits.length}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </>
        ) : (
          <div className="text-center py-12">
            <h3 className="text-lg font-semibold">No units found</h3>
            <p className="text-gray-500 dark:text-gray-400 dark:text-gray-500">
              {searchTerm
                ? "Try a different search term"
                : "Add your first unit to get started"}
            </p>
          </div>
        )}
      </div>

      {/* Add Unit Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Unit</DialogTitle>
            <DialogDescription>
              Create a new measurement unit for your products
            </DialogDescription>
          </DialogHeader>
          <UnitForm
            onSubmit={handleAdd}
            onCancel={() => setShowAddDialog(false)}
            isLoading={isSubmitting}
            mode="add"
          />
        </DialogContent>
      </Dialog>

      {/* Edit Unit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Unit</DialogTitle>
            <DialogDescription>
              Update the details of this unit
            </DialogDescription>
          </DialogHeader>
          <UnitForm
            unit={selectedUnit}
            onSubmit={handleUpdate}
            onCancel={() => setShowEditDialog(false)}
            isLoading={isSubmitting}
            mode="edit"
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Unit</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedUnit?.name}"? This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UnitsPage;
